import React from 'react';
import { Box, Typography, CircularProgress, Button, IconButton } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { Tutorial } from '../types/tutorial';
import api from '../utils/api';
import { NavigateNext } from '@mui/icons-material';

export const TutorialViewerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tutorial, setTutorial] = React.useState<Tutorial | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(0);

  React.useEffect(() => {
    const fetchTutorial = async () => {
      try {
        const response = await api.get<{ data: Tutorial }>(`/tutorials/${id}`);
        setTutorial(response.data.data);
      } catch (err) {
        setError(t('pages.tutorials.errorLoading'));
        console.error('Error fetching tutorial:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTutorial();
  }, [id, t]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (tutorial) {
      setCurrentPage((prev) => Math.min(tutorial.contentImagesPathes.length - 1, prev + 1));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tutorial) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          {error || t('pages.tutorials.notFound')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/tutorials')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexShrink: 0
      }}>
        <Typography variant="h5">{tutorial.title}</Typography>
      </Box>
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative'
      }}>
        <Box sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <img
            src={tutorial.contentImagesPathes[currentPage]}
            alt={`${tutorial.title} - Page ${currentPage + 1}`}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 2px 8px 0 rgba(63,69,81,0.16)'
            }}
          />
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            transform: 'translateY(-50%)',
            padding: '0 16px',
            zIndex: 1
          }}>
            <IconButton 
              onClick={handleNextPage}
              disabled={currentPage === tutorial.contentImagesPathes.length - 1}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <NavigateNext sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
            <IconButton 
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              <NavigateNext />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '8px 16px',
          borderRadius: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <IconButton 
            onClick={handleNextPage}
            disabled={currentPage === tutorial.contentImagesPathes.length - 1}
            size="small"
            sx={{ 
              '&:disabled': {
                opacity: 0.5
              }
            }}
          >
            <NavigateNext sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {t('pages.tutorials.page')} {currentPage + 1} {t('pages.tutorials.of')} {tutorial.contentImagesPathes.length}
          </Typography>
          <IconButton 
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            size="small"
            sx={{ 
              '&:disabled': {
                opacity: 0.5
              }
            }}
          >
            <NavigateNext />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}; 